import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useAuth } from "../../context/AuthContext";
import { LogOut, KeyRound, FileText, Truck, ArrowLeftRight, PackageX } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function MeuPerfil() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [frota, setFrota] = useState([]);

  useEffect(() => {
    api.get("/portal/me").then((r) => setData(r.data));
    // padroniza com ClienteHome: tenta /cliente/minha-frota primeiro, fallback /portal/minha-frota
    api.get("/cliente/minha-frota")
      .then((r) => {
        const items = r.data?.items || [];
        if (items.length > 0) {
          setFrota(items.map((e) => ({
            id: e.id,
            modelo_nome: e.propriedade ? String(e.propriedade).toUpperCase() : "EQUIPAMENTO",
            numero_serie: e.serial,
            status_visual: e.status === "com_cliente" || e.status === "locado" ? "ativo" : (e.status || "ativo"),
          })));
        } else {
          api.get("/portal/minha-frota").then((r2) => setFrota(r2.data.unidades || [])).catch(() => setFrota([]));
        }
      })
      .catch(() => {
        api.get("/portal/minha-frota").then((r) => setFrota(r.data.unidades || [])).catch(() => setFrota([]));
      });
  }, []);

  if (!data) return null;

  const { cliente, contratos, tickets_total, unidades_ativas } = data;
  const contrato = contratos?.[0];
  const venc = contrato?.vencimento ? new Date(contrato.vencimento) : null;
  const hoje = new Date();
  let statusContrato = "Ativo";
  let statusColor = "bg-green-100 text-green-700";
  if (venc) {
    const dias = Math.floor((venc - hoje) / 86400000);
    if (dias < 0) { statusContrato = "Vencido"; statusColor = "bg-red-100 text-red-700"; }
    else if (dias <= 30) { statusContrato = "A vencer"; statusColor = "bg-yellow-100 text-yellow-800"; }
  }

  const initials = (cliente.nome || "?")
    .split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="space-y-5 max-w-4xl">
      {/* Header */}
      <Card className="p-6">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16 border-2" style={{ borderColor: "#F97316" }}>
            {data.user?.picture ? (
              <img src={data.user.picture} alt={cliente.nome} />
            ) : (
              <AvatarFallback
                className="text-white text-lg font-bold"
                style={{ background: "#1B3A6B" }}
              >
                {initials}
              </AvatarFallback>
            )}
          </Avatar>
          <div>
            <h1 className="text-xl font-display font-bold" style={{ color: "#1B3A6B" }}>
              {cliente.nome}
            </h1>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>
        </div>
      </Card>

      {/* Cards de resumo */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wider">
            <Truck size={14} /> Equipamentos ativos
          </div>
          <div className="text-2xl font-display font-bold mt-1" style={{ color: "#1B3A6B" }}>
            {unidades_ativas}
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wider">
            <FileText size={14} /> Chamados totais
          </div>
          <div className="text-2xl font-display font-bold mt-1" style={{ color: "#1B3A6B" }}>
            {tickets_total}
          </div>
        </Card>
      </div>

      {/* Contrato */}
      {contrato && (
        <Card className="p-5" data-testid="perfil-contrato">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-3">
            Informações do contrato
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-xs text-gray-500">Status</div>
              <Badge className={`mt-1 ${statusColor}`}>{statusContrato}</Badge>
            </div>
            <div>
              <div className="text-xs text-gray-500">Vencimento</div>
              <div className="font-medium mt-1">
                {venc ? venc.toLocaleDateString("pt-BR") : "—"}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Valor mensal</div>
              <div className="font-medium mt-1" style={{ color: "#F97316" }}>
                {fmt(contrato.valor_mensal)}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Frota ativa — vem do inventário via contratos */}
      <Card className="p-5" data-testid="perfil-frota">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-2">
            <Truck size={14} /> Frota ativa ({frota.length})
          </h2>
        </div>
        {frota.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-6 border border-dashed rounded">
            Você ainda não possui equipamentos vinculados à sua frota.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Equipamento</TableHead>
                  <TableHead>Nº de série</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {frota.map((u) => {
                  const statusLabel = u.status_visual === "manutencao"
                    ? { label: "Em atendimento", cls: "bg-orange-100 text-orange-700 border-orange-200" }
                    : u.status_visual === "devolvido"
                    ? { label: "Devolvido", cls: "bg-gray-100 text-gray-700 border-gray-200" }
                    : { label: "Locado", cls: "bg-blue-100 text-blue-700 border-blue-200" };
                  return (
                    <TableRow key={u.id} data-testid={`perfil-frota-row-${u.id}`}>
                      <TableCell className="font-medium">{u.modelo_nome || "Equipamento"}</TableCell>
                      <TableCell className="font-mono text-xs">{u.numero_serie}</TableCell>
                      <TableCell><Badge variant="outline" className={statusLabel.cls}>{statusLabel.label}</Badge></TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => nav(`/cliente/chamados/troca?unidade=${u.id}`)}
                          data-testid={`perfil-frota-troca-${u.id}`}
                          title="Solicitar troca"
                        >
                          <ArrowLeftRight size={12} className="mr-1.5" /> Troca
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => nav(`/cliente/chamados/devolucao?unidade=${u.id}`)}
                          data-testid={`perfil-frota-devolucao-${u.id}`}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-300"
                          title="Solicitar devolução"
                        >
                          <PackageX size={12} className="mr-1.5" /> Devolução
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Dados cadastrais */}
      <Card className="p-5" data-testid="perfil-dados">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-3">
          Dados cadastrais
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <Field label="CNPJ/CPF" value={cliente.cnpj_cpf} />
          <Field label="Tipo" value={cliente.tipo === "PJ" ? "Pessoa jurídica" : "Pessoa física"} />
          <Field label="Telefone" value={cliente.telefone || "—"} />
          <Field label="E-mail" value={cliente.email || "—"} />
          <div className="sm:col-span-2">
            <Field label="Endereço" value={cliente.endereco || "—"} />
          </div>
        </div>
      </Card>

      {/* Ações */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => toast.info("Em breve. Fale com seu consultor para redefinir.")}
          data-testid="perfil-alterar-senha"
        >
          <KeyRound size={14} className="mr-2" /> Alterar senha
        </Button>
        <Button
          variant="outline"
          onClick={() => { logout(); nav("/cliente/login"); }}
          data-testid="perfil-sair"
          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
        >
          <LogOut size={14} className="mr-2" /> Sair
        </Button>
      </div>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className="font-medium mt-1 text-gray-800">{value}</div>
  </div>
);
